import React, { useEffect } from 'react';
import PropTypes from 'prop-types';

import Grid from '@material-ui/core/Grid';
import Radio from '@material-ui/core/Radio';
import RadioGroup from '@material-ui/core/RadioGroup';
import FormControlLabel from '@material-ui/core/FormControlLabel';
import FormControl from '@material-ui/core/FormControl';

import { Auth } from 'aws-amplify';

import { useFormik } from 'formik';
import * as Yup from 'yup';

import _ from 'lodash';

import Switch from '../../components/FormFields/Switch';
import useLocalStorage from '../../hooks/useLocalStorage';
import MaskedTextField from '../../components/FormFields/MaskedTextField';
import classes from './AuthHeadersSelector.module.css';

function genMask() {
    let mask = _.chain(Array(36)).fill(/[a-z0-9]/).value();
    mask.splice(8,  1, '-');
    mask.splice(13, 1, '-');
    mask.splice(18, 1, '-');
    mask.splice(23, 1, '-');

    return mask;
}

const MaskProps = {
    mask:              genMask(),
    showMask:          true,
    guide:             true,
    keepCharPositions: true,
};

const validationSchema = Yup.object().shape({
    companyId: Yup
        .string()
        .matches(/[^_]{8}-[^_]{4}-[^_]{4}-[^_]{4}-[^_]{12}/, 'Company id must be exactly 36 characters')
        .required('Company id is required'),
});

const AuthHeadersSelector = ({ handleAuthChange }) => {

    const [devPortal, setDevPortal] = useLocalStorage('devPortal', {
        allowAuth:       false,
        companyType:     'default',
        customCompanyId: '',
    });

    useEffect(() => {
        async function update() {
            // process token
            const session = devPortal.allowAuth ? await Auth.currentSession() : null;
            const token = session ? `Bearer ${session.idToken.jwtToken}` : null;

            // process companyId
            const companyId = getCompanyId();

            handleAuthChange({token, companyId});
        }

        update();
    }, [devPortal]);

    const getCompanyId = (type = devPortal.companyType) => {
        if ('current' === type) return sessionStorage.getItem('companyId');
        if ('other' === type) return devPortal.customCompanyId || '';
        return '';
    };

    function handleSubmit() {
        const companyId = formik.values.companyId;
        setDevPortal({
            ...devPortal,
            customCompanyId: companyId,
        });
    }

    const formik = useFormik({
        initialValues: {
            companyId: getCompanyId(),
        },
        validationSchema,
        onSubmit:           handleSubmit,
        enableReinitialize: true,
    });

    const handleAllowAuth = () => {
        setDevPortal({
            ...devPortal,
            allowAuth: !devPortal.allowAuth,
        });
    };

    const handleCompanyTypeChange = event => {
        const companyType = event.target.value;

        setDevPortal({
            ...devPortal,
            companyType,
        });
    };

    return (
        <div className={classes.AuthHeadersSelector}>
            <Grid container justify="flex-start">
                <Grid item sm={12}>
                    <h1>Auth Scheme</h1>
                </Grid>
                <Grid item sm={5}>
                    <div className={classes.switch}>
                        <Switch
                            label={'Use current session\'s Auth token'}
                            checked={!!devPortal.allowAuth}
                            name="allowAuth"
                            onChange={handleAllowAuth}
                        />
                    </div>
                </Grid>
                <Grid item>
                    <FormControl component="fieldset">
                        <MaskedTextField
                            id="companyId"
                            name="companyId"
                            value={formik.values.companyId}
                            onChange={formik.handleChange}
                            variant="outlined"
                            label="Company Id"
                            FormHelperTextProps={{ classes: { error: classes.error } }}
                            error={Boolean(formik.errors.companyId) && Boolean(formik.touched.companyId)}
                            helperText={Boolean(formik.touched.companyId) && formik.errors.companyId}
                            InputLabelProps={{
                                shrink: true,
                            }}
                            InputProps={{
                                onBlur: evt => {
                                    formik.handleBlur(evt);
                                    formik.handleSubmit();
                                },
                                classes: {
                                    error: classes.cssErrorUnderline,
                                    root:  classes.input,
                                }
                            }}
                            MaskProps={MaskProps}
                            disabled={_.includes(['current', 'default'], devPortal.companyType)}
                        />
                        <RadioGroup
                            aria-label="Company"
                            name="companyType"
                            className={classes.group}
                            row={true}
                            value={devPortal.companyType}
                            onChange={handleCompanyTypeChange}
                        >
                            <FormControlLabel value="current" control={<Radio />} label="Current" />
                            <FormControlLabel value="default" control={<Radio />} label="Default" />
                            <FormControlLabel value="other" control={<Radio />} label="Other" />
                        </RadioGroup>
                    </FormControl>
                </Grid>
            </Grid>
            <hr />
        </div>
    );
};

AuthHeadersSelector.defaultProps = {
    handleAuthChange: () => {},
};

AuthHeadersSelector.propTypes = {
    handleAuthChange: PropTypes.func,
};

export default AuthHeadersSelector;
